import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcInventoryV1TradingDayQueryServiceTradingDayGet } from '@interxfi/api-client/generated/hooks/ixc-inventory-v1-trading-day-query-service-trading-day-get';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcInventoryV1TradingDayState } from '@interxfi/types/generated/api';
import { getInventoryTradeTotalsListGroups } from '../../../../../../../../../../../../features/inventory/shared-inventory-list-items';
import {
  InventoryTradingDayEventDrawer,
  InventoryTradingDayEventSummary,
} from '../../../../../../../../../../../../features/inventory/trading-day/inventory-trading-day-event';
import { getInventoryTradingDayStateListItems } from '../../../../../../../../../../../../features/inventory/trading-day/inventory-trading-day-list-items';
import { getInventoryTradingDayDetailPath } from '../../../../../../../../../../../../features/inventory/trading-day/inventory-trading-day-routing';
import { InventoryTradingDayStatusPill } from '../../../../../../../../../../../../features/inventory/trading-day/inventory-trading-day-status-pill';
import { InventoryTradingDaySummaryBar } from '../../../../../../../../../../../../features/inventory/trading-day/inventory-trading-day-summary-bar';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/operations/_operations/inventory/trading-day/_trading-day/$assetId/_$assetId/$date',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `inventory-trading-day-${params.assetId}-${params.date}`,
        label: params.date,
        to: getInventoryTradingDayDetailPath(params.assetId, params.date),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Inventory Trading Day'),
  component: InventoryTradingDayDetail,
});

function getListItems(data: IxcInventoryV1TradingDayState | undefined) {
  const listItems = getInventoryTradingDayStateListItems(data);

  return {
    general: {
      left: [
        ...(listItems.data.asset.summary ? [listItems.data.asset.summary] : []),
        listItems.data.marketOpen,
        listItems.data.marketClose,
        listItems.data.nextTradingDate,
        listItems.data.settleDate,
        listItems.data.openingTradeQuantity,
        listItems.data.runningTradeQuantity,
      ] satisfies ReadonlyListItem[],
      right: [listItems.metadata.createdAt, listItems.metadata.updatedAt, listItems.assetId, listItems.date] satisfies ReadonlyListItem[],
    },
    totals: {
      groups: getInventoryTradeTotalsListGroups(data?.data?.totals, 'totals.'),
    },
  };
}

function getMenuItems(assetId: string, date: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: InventoryTradingDayEventDrawer,
        props: { request: { assetId, date } },
      },
    },
    actions: undefined,
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load trading day', 'view this trading day');

function InventoryTradingDayDetail() {
  const { assetId, date } = Route.useParams();
  const request = { assetId, date } as const;
  const { data, error, isPending } = useIxcInventoryV1TradingDayQueryServiceTradingDayGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.tradingDay), [data?.tradingDay]);
  const menuItems = useMemo(() => getMenuItems(assetId, date), [assetId, date]);

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <InventoryTradingDayStatusPill isPending={showPending} size="large" status={data?.tradingDay?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <InventoryTradingDaySummaryBar assetId={assetId} date={date} />
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <InventoryTradingDayEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>General</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={7}>
          <VerticalStack gap={4}>
            <Typography.Level3>Totals</Typography.Level3>
            <AppReadonlyListStack groups={listItems.totals.groups} isPending={showPending} titleGap={4} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
